import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

/**
 * Statuses `fetchBaseQuery` reports when the request never got an answer from
 * the server. `PARSING_ERROR` and `CUSTOM_ERROR` are also string statuses but
 * mean the server did answer, so they are deliberately excluded.
 */
const TRANSPORT_FAILURE_STATUSES = new Set(['FETCH_ERROR', 'TIMEOUT_ERROR']);

export const MAX_TRANSPORT_RETRIES = 2;

/** True when the request failed before reaching the server. */
export function isTransportFailure(error: unknown): boolean {
  if (typeof error !== 'object' || error === null || !('status' in error)) {
    return false;
  }
  const { status } = error as FetchBaseQueryError;
  return typeof status === 'string' && TRANSPORT_FAILURE_STATUSES.has(status);
}

/**
 * `extraOptions` fragment that retries only the failures a retry can fix.
 *
 * A numeric status means the server answered, so repeating the request just
 * burns a round trip — a 404 stays a 404. Only a request that never reached the
 * server is worth repeating.
 *
 * The attempt bound lives inside `retryCondition` on purpose: RTK's retry loop
 * exits solely on `retryCondition` returning false, and once a custom condition
 * is supplied `maxRetries` feeds nothing but the backoff curve. A condition
 * without its own bound would retry forever.
 */
export const transportFailureRetry = {
  maxRetries: MAX_TRANSPORT_RETRIES,
  retryCondition: (
    error: FetchBaseQueryError,
    _args: unknown,
    { attempt }: { attempt: number },
  ) => attempt <= MAX_TRANSPORT_RETRIES && isTransportFailure(error),
};
